"use client";

import type * as React from "react";

import { CardSection as CardSectionType, ContentCard } from "@/types/cms";
import SectionHeader from "@/components/sections/utils/SectionHeader";
import CardsGrid from "@/components/cards/cardsGrid/cardsGrid";
import styles from "./CardSection.module.css";
import { getSectionSpacingClasses, normalizeSpace } from "@/components/sections/utils/spacing";
import CardContentItem from "@/components/cards/cardContent/cardContent";
import { Separator } from "@/components/separator/separator";
import SectionButtonsDialog from "@/components/sections/utils/SectionButtonsDialog";
import { bucketPricingCards } from "./pricingBuckets";
import { cardImageSizes, HERO_IMAGE_SIZES, HERO_IMAGE_QUALITY } from "@/lib/cardImageSizes";
// Static imports — all three carousel/tabs consumers share embla-carousel +
// CardSection.module.css, so keeping them in CardSection's chunk lets Turbopack
// bundle embla ONCE. They were previously `next/dynamic` with `ssr: true`,
// which (a) still loaded their chunks on initial render so embla wasn't
// actually deferred, and (b) DUPLICATED embla + the CSS module across the three
// separate dynamic chunks (Lighthouse "Duplicated JavaScript"). One shared copy
// is both smaller and simpler.
import PricingTabs from "./PricingTabs";
import MobileCardCarousel from "./MobileCardCarousel";
import TestimonialCarousel from "./TestimonialCarousel";

// Extra loose CMS fields read by this component that aren't on the shared
// CardSection type (legacy / customID fields the schema also emits).
type CardSectionData = CardSectionType & {
  type?: CardSectionType["type"] | "type_3upCenter";
  cardSection_customID?: string | null;
  callout?: boolean;
  marginTop?: CardSectionType["spaceTop"];
  marginBottom?: CardSectionType["spaceBottom"];
};

interface CardSectionProps {
  data: CardSectionData;
}

// Hoisted out of CardSection's render so it isn't re-created each render
// (react-hooks/static-components). Takes everything it needs as props.
const CardSectionHeader = ({
  data,
  isHeroSection,
  isTestimonialSection,
  isPriceSection,
}: {
  data: CardSectionData;
  isHeroSection: boolean;
  isTestimonialSection: boolean;
  isPriceSection: boolean;
}) => (
  <div
    className={
      isHeroSection
        ? styles.heroHeader
        : isTestimonialSection
        ? styles.testimonialHeader
        : isPriceSection
        ? styles.priceHeader
        : undefined
    }
  >
    <SectionHeader
      content={data.content}
      align={data.contentAlign}
      textAlign={data.textJustify}
      headerWidth={data?.headerWidth}
      eyebrow={data?.eyebrow ?? undefined}
      icon={data?.icon ?? undefined}
    />
  </div>
);

const CardSection = ({ data }: CardSectionProps) => {
  // The hero entrance is now pure CSS (`hero-entrance-*` classes gated by
  // `html[data-entrance]`, see animation.css) — it runs on first paint, not
  // after hydration, so no JS loading-gate is needed here anymore.

  if (!data) return null;

  // Read the CMS custom ID field (cardSection_customID in the schema)
  const rawCustomId = data?.cardSection_customID ?? undefined;
  const customId =
    typeof rawCustomId === "string" && rawCustomId.startsWith("#")
      ? rawCustomId.slice(1)
      : rawCustomId;
  const sectionId = customId || "features";
  const spacing = getSectionSpacingClasses(data);
  const shadowClass = data?.shadow === false ? styles.noShadow : undefined;
  const shadow = data?.shadow !== false; // Default to true unless explicitly false
  const callout = data?.callout === true;
  const widthToken = data?.sectionWidth;
  const widthClass = widthToken ? `w-${widthToken}` : undefined;
  const isHeroSection =
    typeof customId === "string" &&
    customId.trim().toLowerCase().replace(/^#/, "") === "hero";
  const isTestimonialSection =
    typeof customId === "string" &&
    customId.trim().toLowerCase() === "testimonial";
  const isPriceSection =
    typeof customId === "string" &&
    (customId.trim().toLowerCase() === "price" ||
      customId.trim().toLowerCase() === "pricing");
  // Helper to sort cards by order field if present, otherwise by id descending (newest first)
  const sortCards = (cards: ContentCard[]): ContentCard[] => {
    if (!Array.isArray(cards) || cards.length === 0) return cards;
    
    // Check if cards have an explicit order field
    const hasOrderField = cards.some(card => typeof card?.order === 'number');
    
    if (hasOrderField) {
      // Sort by order field if present
      return [...cards].sort((a, b) => {
        const orderA = typeof a?.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
        const orderB = typeof b?.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
    }
    
    // Fallback: sort by id descending (newest first)
    // This helps when the CMS doesn't maintain drag-and-drop order
    return [...cards].sort((a, b) => {
      const idA = typeof a?.id === 'number' ? a.id : 0;
      const idB = typeof b?.id === 'number' ? b.id : 0;
      return idB - idA; // Descending order (newest first)
    });
  };

  // Helper to determine card variant based on section or card customID
  const getCardVariant = (card: ContentCard): "default" | "testimonial" | "price" | "lesson" | "agentAvatar" => {
    const cardCustomId =
      typeof card?.contentCard_customID === "string"
        ? card.contentCard_customID.toLowerCase().trim()
        : undefined;
    // Render the Fulton mascot avatar in place of this card's content.
    if (cardCustomId === "agentavatar") return "agentAvatar";
    if (
      (cardCustomId && cardCustomId.includes("price")) ||
      isPriceSection
    )
      return "price";
    if (cardCustomId === "testimonial" || isTestimonialSection) return "testimonial";
    // Code "Your Lesson Plan" panel in place of the static image.
    if (cardCustomId === "lesson") return "lesson";
    return "default";
  };

  const showDivider = data?.divider === true;

  // Divider-above-space: when a divider is shown, the divider sits flush at the
  // top of the section (drop the section's mt-*) and the spaceTop value becomes
  // the gap *below* the divider instead.
  const spaceTopToken = normalizeSpace(data?.spaceTop ?? data?.marginTop);
  const spaceBottomToken = normalizeSpace(data?.spaceBottom ?? data?.marginBottom);
  const sectionSpacingClass = showDivider
    ? (spaceBottomToken ? `mb-${spaceBottomToken}` : '')
    : spacing;
  const dividerGapClass = spaceTopToken
    ? (styles as Record<string, string>)[`dividerGap${spaceTopToken[0].toUpperCase()}${spaceTopToken.slice(1)}`]
    : '';
  const dividerClass = [styles.divider, dividerGapClass].filter(Boolean).join(' ');

  // Sticky text: in 2-up split layouts, pin the text column while scrolling past
  // a taller image (≥1024px only; inert when text is taller). See .stickyText.
  const stickyText = data?.stickyText === true;
  const stickyClass = stickyText ? styles.stickyText : undefined;

  const sectionClass = [
    'section',
    sectionSpacingClass,
    shadowClass,
  ]
    .filter(Boolean)
    .join(" ");
  const containerClass = [
    'section-container',
    widthClass,
    callout ? styles.calloutInner : undefined,
  ]
    .filter(Boolean)
    .join(' ');
  const columnGapToken = data?.columnGap;
  const gapClass = columnGapToken === 'sm' ? styles.gapSm : columnGapToken === 'lg' ? styles.gapLg : styles.gapMd;
  const overflowBehavior = data?.overflowBehavior;
  const enableCarousel = overflowBehavior === 'carousel' || isTestimonialSection || isPriceSection;

  const buttons = data?.buttons;
  const dialogContent = data?.dialogContent;

  // Sort content_cards once at the top level
  const rawCards = data?.content_cards || [];
  const content_cards = sortCards(rawCards);

  // Per-layout <Image> `sizes` for this section's cards, so each image ships
  // at roughly its rendered width instead of the deviceSizes max. The grid
  // branches (CardsGrid) compute their own; this covers the direct single-card
  // and split-column renders below, which all match `data.type`.
  const cardSizes = cardImageSizes(data.type as string | undefined);

  const headerNode = (
    <CardSectionHeader
      data={data}
      isHeroSection={isHeroSection}
      isTestimonialSection={isTestimonialSection}
      isPriceSection={isPriceSection}
    />
  );
  const renderHeader = () => headerNode;

  // Tabbed pricing: when a price section's cards are tagged into
  // Subscription / One-time groups (via each card's contentCard_customID),
  // render the tab switcher above the cards — mirroring the app paywall's
  // `all` variant. Untagged price sections fall through to the flat layout
  // below, so existing pricing content is unaffected.
  const priceBuckets = isPriceSection ? bucketPricingCards(content_cards) : null;
  if (isPriceSection && priceBuckets?.hasTabs) {
    return (
      <PricingTabs
        sectionId={sectionId}
        sectionClassName={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        containerClassName={[containerClass, gapClass].filter(Boolean).join(' ')}
        showDivider={showDivider}
        dividerClassName={dividerClass}
        header={headerNode}
        gridType={data.type}
        buckets={priceBuckets}
        getCardVariant={getCardVariant}
      />
    );
  }

  // 1up100vh layout: single image filling grid with 90vh max height
  if (data.type === 'type_1up100vh') {
    const firstCard = content_cards[0];

    return (
      <section
        id={customId || 'one-up'}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={containerClass}>
          {showDivider && (
            <div className={dividerClass}><Separator /></div>
          )}
          <div className={styles.oneUpWrap}>
            {firstCard && <CardContentItem card={firstCard} shadow={shadow} variant={getCardVariant(firstCard)} imageSizes={cardSizes} />}
          </div>
        </div>
      </section>
    );
  }

  // 1up standard: render the single card at full section width
  if (data.type === 'type_1up') {
    const firstCard = content_cards[0];
    // Carousel overflow: render cards horizontally using Carousel primitives
    if (enableCarousel && content_cards.length > 1) {
      return (
        <section
          id={customId || 'one-up'}
          className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        >
          <div className={containerClass}>
            {showDivider && (
              <div className={dividerClass}><Separator /></div>
            )}
            {headerNode}
            <MobileCardCarousel cards={content_cards} getCardVariant={getCardVariant} shadow={shadow} imageSizes={cardSizes} />
            <div className={styles.hideSm}>
              {firstCard && (
                <div>
                  <CardContentItem card={firstCard} shadow={shadow} variant={isTestimonialSection ? "testimonial" : "default"} imageSizes={cardSizes} />
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    // Default: wrap behavior (single card full width)
    return (
      <section
        id={customId || 'one-up'}
        className={[
          callout ? `${sectionClass} ${styles.callout}` : sectionClass,
          isHeroSection ? styles.heroSection : undefined,
        ].filter(Boolean).join(' ')}
      >
        <div
          className={[
            containerClass,
            gapClass,
            isHeroSection ? styles.heroInner : undefined,
          ].filter(Boolean).join(' ')}
        >
          {showDivider && (
            <div className={dividerClass}><Separator /></div>
          )}
          {isHeroSection ? (
            <div className="hero-entrance-title">
              {headerNode}
            </div>
          ) : (
            headerNode
          )}
          {Array.isArray(buttons) && buttons.length > 0 && (
            isHeroSection ? (
              <div className={`hero-entrance-button ${styles.heroButtons}`}>
                <SectionButtonsDialog
                  buttons={buttons}
                  dialogContent={dialogContent}
                  align={data.contentAlign}
                  prioritizePrimary={isHeroSection}
                />
              </div>
            ) : (
              <SectionButtonsDialog
                buttons={buttons}
                dialogContent={dialogContent}
                align={data.contentAlign}
                prioritizePrimary={isHeroSection}
              />
            )
          )}
          {firstCard && (
            isHeroSection ? (
              <div className="hero-entrance-image">
                {/* Padding handled by `.heroCard` via --container-padding-x
                   (24px mobile / 32px tablet / 48px desktop). No inline
                   override — hardcoding 48px made the mobile image tiny. */}
                <div className={`${styles.heroCardSpacing} ${styles.heroCard}`}>
                  <CardContentItem card={firstCard} shadow={shadow} variant={getCardVariant(firstCard)} imageSizes={HERO_IMAGE_SIZES} priority imageQuality={HERO_IMAGE_QUALITY} />
                </div>
              </div>
            ) : (
              <div>
                <CardContentItem card={firstCard} shadow={shadow} variant={getCardVariant(firstCard)} imageSizes={cardSizes} />
              </div>
            )
          )}
        </div>
      </section>
    );
  }

  // 2up 50/50
  if (data.type === 'type_2up50/50') {
    const cardsArr = content_cards;
    if (enableCarousel && cardsArr.length > 0) {
      return (
        <section
          id={customId || "two-up"}
          className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        >
          <div className={containerClass}>
            {showDivider && (
              <div className={dividerClass}><Separator /></div>
            )}
            {headerNode}
            <MobileCardCarousel cards={cardsArr} getCardVariant={getCardVariant} shadow={shadow} imageSizes={cardSizes} />
            <div className={styles.hideSm}>
              {/* fall back to existing single/dual layout on larger screens */}
              {cardsArr.length === 1 ? (
                <div className={styles.twoUp5050Grid}>
                  <div className={[styles.twoUp5050Text, stickyClass].filter(Boolean).join(' ')}>
                    {headerNode}
                  </div>
                  <div className={`reveal-item ${styles.twoUp5050Image}`} style={{ "--reveal-index": 1 } as React.CSSProperties}>
                    <CardContentItem card={cardsArr[0]} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={cardSizes} />
                  </div>
                </div>
              ) : (
                <CardsGrid type={data.type} cards={[...cardsArr]} shadow={shadow} getCardVariant={getCardVariant} />
              )}
            </div>
          </div>
        </section>
      );
    }
    // If only one card, render like the 60/40/40/60 variants: header + image side-by-side
    if (cardsArr.length === 1) {
      const firstCard = cardsArr[0];
      return (
        <section id={customId || "two-up"} className={sectionClass}>
        <div className={['section-container', widthClass, gapClass].filter(Boolean).join(' ')}>
          {showDivider && (
            <div className={dividerClass}><Separator /></div>
          )}
            <div className={styles.twoUp5050Grid}>
              <div className={[styles.twoUp5050Text, stickyClass].filter(Boolean).join(' ')}>
                <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={data?.headerWidth} eyebrow={data?.eyebrow ?? undefined} icon={data?.icon ?? undefined} />
              </div>
              <div className={`reveal-item ${styles.twoUp5050Image}`} style={{ "--reveal-index": 1 } as React.CSSProperties}>
                <CardContentItem card={firstCard} shadow={shadow} variant={getCardVariant(firstCard)} imageSizes={cardSizes} />
              </div>
            </div>
          </div>
        </section>
      );
    }
    // Sticky-text 2-card case: 50/50 normally routes 2+ cards through CardsGrid,
    // which has no column roles a column could pin against. When stickyText is on
    // and there are exactly two cards, render the dedicated two-column grid so the
    // shorter column can stick. stickyClass goes on BOTH columns — only the
    // shorter one actually pins (the taller fills the row, so its sticky is inert),
    // which stays correct regardless of card order.
    if (stickyText && cardsArr.length === 2) {
      return (
        <section
          id={customId || "two-up"}
          className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        >
          <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
            {showDivider && (
              <div className={dividerClass}><Separator /></div>
            )}
            {headerNode}
            <div className={styles.twoUp5050Grid}>
              <div className={['reveal-item', styles.twoUp5050Text, stickyClass].filter(Boolean).join(' ')} style={{ "--reveal-index": 0 } as React.CSSProperties}>
                <CardContentItem card={cardsArr[0]} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={cardSizes} />
              </div>
              <div className={['reveal-item', styles.twoUp5050Image, stickyClass].filter(Boolean).join(' ')} style={{ "--reveal-index": 1 } as React.CSSProperties}>
                <CardContentItem card={cardsArr[1]} shadow={shadow} variant={getCardVariant(cardsArr[1])} imageSizes={cardSizes} />
              </div>
            </div>
          </div>
        </section>
      );
    }
    // Otherwise render standard header + cards grid
    return (
      <section
        id={customId || "two-up"}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
          {showDivider && (
            <div className={dividerClass}><Separator /></div>
          )}
          {headerNode}
          {Array.isArray(buttons) && buttons.length > 0 && (
            <SectionButtonsDialog
              buttons={buttons}
              dialogContent={dialogContent}
              align={data.contentAlign}
              prioritizePrimary={isHeroSection}
            />
          )}
          <CardsGrid type={data.type} cards={[...cardsArr]} shadow={shadow} getCardVariant={getCardVariant} />
        </div>
      </section>
    );
  }

  // 3up Center: render cards in a single centered column matching header width
  if (data.type === 'type_3upCenter') {
    if (enableCarousel && content_cards.length > 0) {
      return (
        <section
          id={customId || "three-up-center"}
          className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        >
          <div className={containerClass}>
            {showDivider && (
              <div className={dividerClass}><Separator /></div>
            )}
            {headerNode}
            <MobileCardCarousel cards={content_cards} getCardVariant={getCardVariant} shadow={shadow} imageSizes={cardSizes} />
            <div className={styles.hideSm}>
              <div className={styles.centerColumn}>
                {content_cards.map((card: ContentCard, idx: number) => (
                <CardContentItem key={card.id ?? `${idx}-${card.header || 'card'}`} card={card} shadow={shadow} variant={getCardVariant(card)} imageSizes={cardSizes} />
                ))}
              </div>
            </div>
          </div>
        </section>
      );
    }
    return (
      <section
        id={customId || "three-up-center"}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={containerClass}>
          {showDivider && (
            <div className={dividerClass}><Separator /></div>
          )}
          {headerNode}
          {Array.isArray(buttons) && buttons.length > 0 && (
            <SectionButtonsDialog
              buttons={buttons}
              dialogContent={dialogContent}
              align={data.contentAlign}
              prioritizePrimary={isHeroSection}
            />
          )}
          <div className={styles.centerColumn}>
            {content_cards.map((card: ContentCard, idx: number) => (
                <CardContentItem key={card.id ?? `${idx}-${card.header || 'card'}`} card={card} shadow={shadow} variant={getCardVariant(card)} imageSizes={cardSizes} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // type_2up60/40: two cards side-by-side with header above; single card pairs with header
  if (data.type === 'type_2up60/40') {
    const cardsArr = content_cards;
    if (enableCarousel && cardsArr.length > 0) {
      return (
        <section
          id={customId || "two-up"}
          className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        >
          <div className={containerClass}>
            {showDivider && (
              <div className={dividerClass}><Separator /></div>
            )}
            {headerNode}
            <MobileCardCarousel cards={cardsArr} getCardVariant={getCardVariant} shadow={shadow} imageSizes={cardSizes} />
            <div className={styles.hideSm}>
              {cardsArr.length >= 2 ? (
                <>
                  <div className={styles.twoUp6040Grid}>
                    <div className={['reveal-item', styles.twoUp6040Text, stickyClass].filter(Boolean).join(' ')} style={{ "--reveal-index": 0 } as React.CSSProperties}>
                      <CardContentItem card={cardsArr[0]} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={cardSizes} />
                    </div>
                    <div className={`reveal-item ${styles.twoUp6040Image}`} style={{ "--reveal-index": 1 } as React.CSSProperties}>
                      <CardContentItem card={cardsArr[1]} shadow={shadow} variant={getCardVariant(cardsArr[1])} imageSizes={cardSizes} />
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.twoUp6040Grid}>
                  <div className={[styles.twoUp6040Text, stickyClass].filter(Boolean).join(' ')}>
                    <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={data?.headerWidth} eyebrow={data?.eyebrow ?? undefined} icon={data?.icon ?? undefined} />
                  </div>
                  <div className={`reveal-item ${styles.twoUp6040Image}`} style={{ "--reveal-index": 1 } as React.CSSProperties}>
                    {cardsArr[0] && <CardContentItem card={cardsArr[0]} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={cardSizes} />}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    return (
      <section
        id={customId || "two-up"}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
          {showDivider && (
            <div className={dividerClass}><Separator /></div>
          )}
          {cardsArr.length >= 2 ? (
            <>
              <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={data?.headerWidth} eyebrow={data?.eyebrow ?? undefined} icon={data?.icon ?? undefined} />
              <div className={styles.twoUp6040Grid}>
                <div className={['reveal-item', styles.twoUp6040Text, stickyClass].filter(Boolean).join(' ')} style={{ "--reveal-index": 0 } as React.CSSProperties}>
                  <CardContentItem card={cardsArr[0]} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={cardSizes} />
                </div>
                <div className={`reveal-item ${styles.twoUp6040Image}`} style={{ "--reveal-index": 1 } as React.CSSProperties}>
                  <CardContentItem card={cardsArr[1]} shadow={shadow} variant={getCardVariant(cardsArr[1])} imageSizes={cardSizes} />
                </div>
              </div>
            </>
          ) : (
            <div className={styles.twoUp6040Grid}>
              <div className={[styles.twoUp6040Text, stickyClass].filter(Boolean).join(' ')}>
                <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={data?.headerWidth} eyebrow={data?.eyebrow ?? undefined} icon={data?.icon ?? undefined} />
              </div>
              <div className={`reveal-item ${styles.twoUp6040Image}`} style={{ "--reveal-index": 1 } as React.CSSProperties}>
                {cardsArr[0] && <CardContentItem card={cardsArr[0]} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={cardSizes} />}
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  // 2up 40/60: two cards with header above; single card pairs with header
  if (data.type === 'type_2up40/60') {
    const cardsArr = content_cards;
    if (enableCarousel && cardsArr.length > 0) {
      return (
        <section
          id={customId || "two-up"}
          className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        >
          <div className={containerClass}>
            {showDivider && (
              <div className={dividerClass}><Separator /></div>
            )}
            <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={data?.headerWidth} eyebrow={data?.eyebrow ?? undefined} icon={data?.icon ?? undefined} />
            <MobileCardCarousel cards={cardsArr} getCardVariant={getCardVariant} shadow={shadow} imageSizes={cardSizes} />
            <div className={styles.hideSm}>
              {cardsArr.length >= 2 ? (
                <>
                  <div className={styles.twoUp4060Grid}>
                    <div className={`reveal-item ${styles.twoUp4060Image}`} style={{ "--reveal-index": 1 } as React.CSSProperties}>
                      <CardContentItem card={cardsArr[0]} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={cardSizes} />
                    </div>
                    <div className={['reveal-item', styles.twoUp4060Text, stickyClass].filter(Boolean).join(' ')} style={{ "--reveal-index": 0 } as React.CSSProperties}>
                      <CardContentItem card={cardsArr[1]} shadow={shadow} variant={getCardVariant(cardsArr[1])} imageSizes={cardSizes} />
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.twoUp4060Grid}>
                  <div className={`reveal-item ${styles.twoUp4060Image}`} style={{ "--reveal-index": 1 } as React.CSSProperties}>
                    {cardsArr[0] && <CardContentItem card={cardsArr[0]} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={cardSizes} />}
                  </div>
                  <div className={[styles.twoUp4060Text, stickyClass].filter(Boolean).join(' ')}>
                    <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={data?.headerWidth} eyebrow={data?.eyebrow ?? undefined} icon={data?.icon ?? undefined} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    return (
      <section
        id={customId || "two-up"}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
          {showDivider && (
            <div className={dividerClass}><Separator /></div>
          )}
          {cardsArr.length >= 2 ? (
            <>
              <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={data?.headerWidth} eyebrow={data?.eyebrow ?? undefined} icon={data?.icon ?? undefined} />
              <div className={styles.twoUp4060Grid}>
                <div className={`reveal-item ${styles.twoUp4060Image}`} style={{ "--reveal-index": 1 } as React.CSSProperties}>
                  <CardContentItem card={cardsArr[0]} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={cardSizes} />
                </div>
                <div className={['reveal-item', styles.twoUp4060Text, stickyClass].filter(Boolean).join(' ')} style={{ "--reveal-index": 0 } as React.CSSProperties}>
                  <CardContentItem card={cardsArr[1]} shadow={shadow} variant={getCardVariant(cardsArr[1])} imageSizes={cardSizes} />
                </div>
              </div>
            </>
          ) : (
            <div className={styles.twoUp4060Grid}>
              <div className={`reveal-item ${styles.twoUp4060Image}`} style={{ "--reveal-index": 1 } as React.CSSProperties}>
                {cardsArr[0] && <CardContentItem card={cardsArr[0]} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={cardSizes} />}
              </div>
              <div className={[styles.twoUp4060Text, stickyClass].filter(Boolean).join(' ')}>
                <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={data?.headerWidth} eyebrow={data?.eyebrow ?? undefined} icon={data?.icon ?? undefined} />
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Showcase: first card is a large media on one side, remaining cards stack
  // as a feature list on the other side. Two orientations via the type value.
  if (data.type === 'type_showcaseLeft' || data.type === 'type_showcaseRight') {
    const isMediaLeft = data.type === 'type_showcaseLeft';
    const [mediaCard, ...listCards] = content_cards;
    const gridClass = isMediaLeft ? styles.showcaseLeftGrid : styles.showcaseRightGrid;
    return (
      <section
        id={customId || "showcase"}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
          {showDivider && (
            <div className={dividerClass}><Separator /></div>
          )}
          {headerNode}
          {Array.isArray(buttons) && buttons.length > 0 && (
            <SectionButtonsDialog
              buttons={buttons}
              dialogContent={dialogContent}
              align={data.contentAlign}
              prioritizePrimary={isHeroSection}
            />
          )}
          <div className={gridClass}>
            <div className={`reveal-item ${styles.showcaseMedia}`} style={{ "--reveal-index": 1 } as React.CSSProperties}>
              {mediaCard && (
                <CardContentItem card={mediaCard} shadow={shadow} variant={getCardVariant(mediaCard)} imageSizes={cardSizes} />
              )}
            </div>
            <div className={`reveal-item ${styles.showcaseList}`} style={{ "--reveal-index": 2 } as React.CSSProperties}>
              {listCards.map((card: ContentCard, idx: number) => (
                <CardContentItem
                  key={card.id ?? `${idx}-${card.header || 'card'}`}
                  card={card}
                  shadow={shadow}
                  variant={getCardVariant(card)}
                  imageSizes={cardSizes}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default grid-based layouts (e.g., 3up/4up)
  if (enableCarousel && content_cards.length > 0) {
    // For testimonials, show carousel on all screen sizes with peek effect (full-bleed).
    // Pricing sections should respect sectionWidth on desktop, so they use the normal
    // "mobile carousel + desktop grid" rendering below.
    if (isTestimonialSection) {
      return (
        <TestimonialCarousel
          content_cards={content_cards}
          getCardVariant={getCardVariant}
          sectionId={sectionId}
          sectionClass={sectionClass}
          containerClass={containerClass}
          callout={callout}
          showDivider={showDivider}
          dividerClass={dividerClass}
          Header={renderHeader}
        />
      );
    }

    // Regular carousel: mobile only, desktop shows grid
    return (
      <section
        id={sectionId}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
          {showDivider && (
            <div className={dividerClass}><Separator /></div>
          )}
          {headerNode}
          <MobileCardCarousel cards={content_cards} getCardVariant={getCardVariant} imageSizes={cardSizes} />
          <div className={styles.hideSm}>
            <CardsGrid
              type={data.type}
              cards={content_cards}
              getCardVariant={getCardVariant}
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id={sectionId}
      className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
    >
      <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
        {showDivider && (
          <div className={dividerClass}><Separator /></div>
        )}
        <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={data?.headerWidth} eyebrow={data?.eyebrow ?? undefined} icon={data?.icon ?? undefined} />
        {Array.isArray(buttons) && buttons.length > 0 && (
          <SectionButtonsDialog
            buttons={buttons}
            dialogContent={dialogContent}
            align={data.contentAlign}
            prioritizePrimary={isHeroSection}
          />
        )}
        <CardsGrid
          type={data.type}
          cards={content_cards}
          getCardVariant={getCardVariant}
        />
      </div>
    </section>
  );
};

export default CardSection; 